import { createSlice } from "@reduxjs/toolkit";
// eslint-disable-next-line no-duplicate-imports
import type { PayloadAction } from "@reduxjs/toolkit";

interface LoanRequestState {
  customerLimit: any;
  addNewBankAccountInProgress: boolean;
  issuedLoanDetails: any;
  hasSeenLimitModal: boolean;
  formLoading?: boolean;
  holidays?: string[];
  communicationPreferences?: any;
  errorMsg?: string;
  currentTime?: string;
  reviewLink?: string;
  availableDates?: string[];
  loanRequestCardSelectionVisibility?: boolean;
  cards?: any[];
  agreementType?: string;
  termsAndConditions?: string;
  loanExplanation?: any;
  customerAccounts?: any[];
  nextXDaysOfPayment?: any;
  loansBasedOnInstallment?: any[];
  loansBasedOnInstallmentWithDefinitionId?: any[];
  areInstallmentOptionsInProgress?: boolean;
  eligibleProductsOfCustomer?: any;
  customerHasIssuedOrArrangedLoan?: boolean;
  repaidLoanCount?: number;
}

const initialState: LoanRequestState = {
  formLoading: true,
  customerLimit: {},
  addNewBankAccountInProgress: false,
  issuedLoanDetails: {},
  hasSeenLimitModal: false
};

const loanRequestSlice = createSlice({
  name: "loanRequest",
  initialState,
  reducers: {
    togglePayloadFormLoading(state, action) {
      state.formLoading = action.payload;
    },
    getHolidaysList(state, action) {
      state.holidays = action.payload;
    },
    getCommunicationPreferences(state, action) {
      state.communicationPreferences = action.payload;
    },
    setErrorMsg(state, action) {
      state.errorMsg = action.payload;
    },
    passCurrentTime(state, action) {
      state.currentTime = action.payload;
    },
    getReviewLink(state, action) {
      state.reviewLink = action.payload;
    },
    toggleLoanRequestCardSelectionVisibility(state, action) {
      state.loanRequestCardSelectionVisibility = action.payload;
    },
    getCards(state, action) {
      state.cards = action.payload;
    },
    getAgreementsStart(state, action) {
      state.agreementType = action.payload.agreementType;
    },
    getAgreementsSuccess(state, action) {
      state.agreementType = "";
      state.termsAndConditions = action.payload.termsAndConditions;
      state.loanExplanation = action.payload.loanExplanation;
    },
    getCustomerLimit(state, action) {
      state.customerLimit = action.payload;
    },
    passCustomerAccounts(state, action) {
      state.customerAccounts = action.payload;
    },
    getNextXDaysOfPayment(state, action) {
      state.nextXDaysOfPayment = action.payload;
    },
    getLoansBasedOnInstallmentLengthRequest(state, action) {
      state.loansBasedOnInstallment = undefined;
      state.areInstallmentOptionsInProgress = true;
    },
    getLoansBasedOnInstallmentLengthSuccess(state, action) {
      state.loansBasedOnInstallment = action.payload;
      state.areInstallmentOptionsInProgress = false;
    },
    toggleAddNewBankAccountInProgress(state, action) {
      state.addNewBankAccountInProgress = action.payload;
    },
    setLoanEligibleProductsOfCustomer(state, action) {
      state.eligibleProductsOfCustomer = action.payload;
    },
    getLoansBasedOnInstallmentLengthWithDefinitionIdRequest(state, action) {
      state.loansBasedOnInstallment = action.payload;
    },
    getLoansBasedOnInstallmentLengthWithDefinitionIdSuccess(state, action) {
      state.loansBasedOnInstallmentWithDefinitionId = action.payload;
    },
    getIssuedLoanForSuccessPage(state, action) {
      state.issuedLoanDetails = action.payload;
    },
    getCustomerHasIssuedOrArrangedLoan(state, action) {
      state.customerHasIssuedOrArrangedLoan = action.payload;
    },
    setHasSeenLimitModal(state, action) {
      state.hasSeenLimitModal = action.payload;
    },
    setRepaidLoanCount(state, action) {
      state.repaidLoanCount = action.payload;
    },
    triggerCreateReviewLink: {
      reducer: (state) => state,
      prepare: (loanId, customerId) => {
        return {
          payload: {
            loanId,
            customerId
          }
        };
      }
    },
    loanRequestSuccess(state) {
      return state;
    },
    agreementAssignedSuccess(state) {
      return state;
    },
    onCloseRepaidLoansMoreThanThreeInOneDayModal(state) {
      return state;
    },
    onCloseConfirmAccountOwnerModal(state) {
      return state;
    },
    calculateLoanDetailsRequested(state, action) {
      state.formLoading = true;
    },
    calculateLoanDetailsSucceeded(state, action) {
      state.formLoading = false;
    },
    calculateLoanDetailsFailed(state, action) {
      state.formLoading = false;
    },
    submitLoanRequestUpdateEmailForm(state, action: PayloadAction<any>) {
      return state;
    },
    destroy() {
      return initialState;
    }
  }
});

export default loanRequestSlice.reducer;
export const {
  togglePayloadFormLoading,
  getHolidaysList,
  getCommunicationPreferences,
  setErrorMsg,
  passCurrentTime,
  getReviewLink,
  toggleLoanRequestCardSelectionVisibility,
  getCards,
  getAgreementsStart,
  getAgreementsSuccess,
  getCustomerLimit,
  passCustomerAccounts,
  getNextXDaysOfPayment,
  getLoansBasedOnInstallmentLengthRequest,
  getLoansBasedOnInstallmentLengthSuccess,
  toggleAddNewBankAccountInProgress,
  setLoanEligibleProductsOfCustomer,
  getLoansBasedOnInstallmentLengthWithDefinitionIdRequest,
  getLoansBasedOnInstallmentLengthWithDefinitionIdSuccess,
  getIssuedLoanForSuccessPage,
  getCustomerHasIssuedOrArrangedLoan,
  setHasSeenLimitModal,
  triggerCreateReviewLink,
  loanRequestSuccess,
  agreementAssignedSuccess,
  onCloseConfirmAccountOwnerModal,
  calculateLoanDetailsRequested,
  calculateLoanDetailsSucceeded,
  calculateLoanDetailsFailed,
  submitLoanRequestUpdateEmailForm,
  destroy
} = loanRequestSlice.actions;
export const loanRequestActions = loanRequestSlice.actions;
